import type { Firestore } from "@firebase/firestore";
import type { FirebaseApp } from "firebase/app";
import type { Auth } from "firebase/auth";
import type { FirebaseStorage } from "firebase/storage";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  messagingSenderId: import.meta.env.VITE_FIREBASE_SENDER_ID,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
};

// // Check environment
const isDevelopment = import.meta.env.MODE === "development";
const useEmulator = import.meta.env.VITE_USE_SERVICES_EMULATOR === "true";

// Initialize variables
let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;

let initializationPromise: Promise<void> | null = null;
let isInitialized = false;

// Deferred initialization function with dynamic imports
async function initializeFirebaseServices() {
  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async () => {
    // Use requestIdleCallback to defer until browser is idle
    await new Promise((resolve) => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(() => resolve(undefined));
      } else {
        setTimeout(resolve, 100);
      }
    });

    try {
      // Dynamically import Firebase modules (won't be in initial bundle)
      const [
        { initializeApp },
        {
          initializeFirestore,
          persistentLocalCache,
          persistentMultipleTabManager,
          connectFirestoreEmulator,
        },
        {
          initializeAuth,
          indexedDBLocalPersistence,
          browserLocalPersistence,
          connectAuthEmulator,
        },
        { getStorage, connectStorageEmulator },
      ] = await Promise.all([
        import("firebase/app"),
        import("firebase/firestore"),
        import("firebase/auth"),
        import("firebase/storage"),
      ]);

      // Initialize Firebase App
      app = initializeApp(firebaseConfig);
      console.log(
        `[Firebase] ✅ App initialized successfully`,
        `\n[Firebase] 🎯 Connected to project: ${firebaseConfig.projectId}`,
        `\n[Firebase] 🔧 Environment: ${import.meta.env.MODE}`,
      );

      // Initialize Firestore Service
      if (app && firebaseConfig.projectId) {
        try {
          db = initializeFirestore(app, {
            localCache: persistentLocalCache({
              tabManager: persistentMultipleTabManager(),
            }),
          });
          console.log("✅ Firestore service initialized with persistent cache");

          if (useEmulator && isDevelopment && db) {
            try {
              connectFirestoreEmulator(db, "localhost", 8080);
              console.log("🔧 Connected to Firestore emulator");
            } catch {
              // Emulator might already be connected
            }
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          console.error("❌ Failed to initialize Firestore:", errorMessage);
        }
      }

      // Initialize Auth Service
      if (app) {
        try {
          auth = initializeAuth(app, {
            persistence: [indexedDBLocalPersistence, browserLocalPersistence],
            popupRedirectResolver: undefined,
          });

          if (useEmulator && isDevelopment && auth) {
            try {
              connectAuthEmulator(auth, "http://localhost:9099", {
                disableWarnings: true,
              });
              console.log("🔧 Connected to Auth emulator");
            } catch {
              // Emulator might already be connected
            }
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          console.error("❌ Failed to initialize Auth:", errorMessage);
        }
      }

      // Initialize Storage Service
      if (
        app &&
        firebaseConfig.storageBucket &&
        firebaseConfig.storageBucket !== "demo-project.appspot.com"
      ) {
        try {
          storage = getStorage(app);
          console.log("✅ Storage service initialized");

          if (useEmulator && isDevelopment && storage) {
            try {
              connectStorageEmulator(storage, "localhost", 9199);
              console.log("🔧 Connected to Storage emulator");
            } catch {
              // Emulator might already be connected
            }
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          console.warn("⚠️ Failed to initialize Storage:", errorMessage);
        }
      }

      isInitialized = true;
    } catch (error) {
      console.error("[Firebase] ❌ Failed to initialize app:", error);
    }
  })();

  return initializationPromise;
}

// Auto-initialize on module load but deferred
initializeFirebaseServices();

// Helper function to check if services are available
export const isServiceAvailable = (
  service: "auth" | "firestore" | "storage",
): boolean => {
  switch (service) {
    case "auth":
      return auth !== null;
    case "firestore":
      return db !== null;
    case "storage":
      return storage !== null;
    default:
      return false;
  }
};

// Helper function to get Firebase app
export const getFirebaseApp = (): FirebaseApp | null => app;

// Helper function to ensure Firebase is initialized
export const ensureFirebaseInitialized = () => initializeFirebaseServices();

// Helper to check if initialization is complete
export const isFirebaseInitialized = () => isInitialized;

// Export the initialized services (will be null until initialized)
export { app, auth, storage, db };

// Export a default object for convenience
export default {
  app,
  auth,
  db,
  isServiceAvailable,
  storage,
  ensureFirebaseInitialized,
  isFirebaseInitialized,
};
